import { useEffect, useMemo, useState } from "react";
import { api, formatErr, fmt } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Plus, Pencil, Trash2, Search, Megaphone, Percent, TrendingUp, Users as UsersIcon, BadgeCheck, BarChart3, Wallet, MinusCircle,
} from "lucide-react";
import { PageHeader, ExportBar } from "@/components/EntityPage";

function AgentForm({ initial, users, onSubmit }) {
  const today = new Date().toISOString().slice(0, 10);
  const [f, setF] = useState(initial || { entry_date: today, commission_pct: 0 });
  const [copyWA, setCopyWA] = useState(!!initial && initial.mobile && initial.mobile === initial.whatsapp);
  const set = (k) => (e) => setF({ ...f, [k]: e.target ? e.target.value : e });
  function onMobileChange(e) {
    const v = e.target.value;
    setF((s) => ({ ...s, mobile: v, whatsapp: copyWA ? v : s.whatsapp }));
  }
  function toggleCopy(v) {
    setCopyWA(v);
    if (v) setF((s) => ({ ...s, whatsapp: s.mobile || "" }));
  }
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit({ ...f, commission_pct: parseFloat(f.commission_pct) || 0 });
      }}
      className="space-y-3 max-h-[70vh] overflow-y-auto pr-2"
    >
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Date</Label>
          <Input type="date" value={f.entry_date || today} onChange={set("entry_date")} data-testid="sa-date" className="bg-[hsl(224_40%_12%)] mt-1"/>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Full name</Label>
          <Input required value={f.name || ""} onChange={set("name")} data-testid="sa-name" className="bg-[hsl(224_40%_12%)] mt-1"/>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Employee ID</Label>
          <Input required value={f.employee_id || ""} onChange={set("employee_id")} placeholder="e.g. ANE-SM-001" data-testid="sa-empid" className="bg-[hsl(224_40%_12%)] mt-1"/>
          <div className="text-[10px] text-muted-foreground mt-1">Manually assigned (no auto-fill)</div>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Email</Label>
          <Input required type="email" value={f.email || ""} onChange={set("email")} disabled={!!initial} data-testid="sa-email" className="bg-[hsl(224_40%_12%)] mt-1 disabled:opacity-70"/>
          {!initial && <div className="text-[10px] text-muted-foreground mt-1">A login user will be auto-created with role <span className="text-amber-300">sales</span> and initial password <span className="font-mono text-amber-300">Change@123</span></div>}
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Mobile</Label>
          <Input value={f.mobile || ""} onChange={onMobileChange} data-testid="sa-mobile" className="bg-[hsl(224_40%_12%)] mt-1"/>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">WhatsApp</Label>
          <Input value={f.whatsapp || ""} onChange={set("whatsapp")} disabled={copyWA} data-testid="sa-whatsapp" className={`bg-[hsl(224_40%_12%)] mt-1 ${copyWA ? "opacity-70" : ""}`}/>
          <label className="mt-1.5 flex items-center gap-2 text-[11px] text-slate-300 cursor-pointer">
            <input type="checkbox" checked={copyWA} onChange={(e) => toggleCopy(e.target.checked)} className="accent-amber-500"/>
            Same as mobile — auto-copy to WhatsApp
          </label>
        </div>
        <div className="col-span-2 grid grid-cols-[1fr_auto] items-end gap-3 border-t border-border pt-3">
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Commission %</Label>
            <Input type="number" step="0.01" min="0" max="100" value={f.commission_pct ?? 0} onChange={set("commission_pct")} data-testid="sa-comm" className="bg-[hsl(224_40%_12%)] mt-1"/>
            <div className="text-[10px] text-muted-foreground mt-1">Applied to <span className="text-emerald-300">Total Profit</span> from trips linked to this agent</div>
          </div>
          <div className="inline-flex items-center gap-1 text-amber-300 font-mono text-lg pb-1"><Percent className="w-4 h-4"/> {parseFloat(f.commission_pct || 0).toFixed(2)}%</div>
        </div>
        <div className="col-span-2 border-t border-border pt-3">
          <Label className="text-[10px] uppercase text-amber-300/80">Link to login account (role: sales &amp; marketing)</Label>
          <select
            value={f.linked_user_id || ""}
            onChange={(e) => setF({ ...f, linked_user_id: e.target.value || null })}
            data-testid="sa-linked-user"
            className="w-full mt-1 h-9 rounded-md bg-[hsl(224_40%_12%)] border border-border text-sm px-2"
          >
            <option value="">— not linked (auto-create user) —</option>
            {(users || []).map((u) => <option key={u.id} value={u.id}>{u.name} · {u.email}</option>)}
          </select>
          <div className="text-[10px] text-muted-foreground mt-1">Pick the login account (role: <span className="text-amber-300">sales</span>) created in Users &amp; Roles. Once linked, that user sees this agent's leads, confirmed vehicles, KPIs and commission on their Sales Dashboard.</div>
        </div>
      </div>
      <div className="flex justify-end">
        <Button type="submit" data-testid="sa-save" className="bg-amber-500 text-[hsl(224_60%_6%)] hover:bg-amber-400 font-semibold">
          {initial ? "Update agent" : "Create agent"}
        </Button>
      </div>
    </form>
  );
}

function StatTile({ icon: Icon, label, value, tone = "amber", testid }) {
  const tones = {
    amber: "text-amber-300 border-amber-500/40 bg-amber-500/[0.06]",
    emerald: "text-emerald-300 border-emerald-500/40 bg-emerald-500/[0.06]",
    sky: "text-sky-300 border-sky-500/40 bg-sky-500/[0.06]",
    pink: "text-pink-300 border-pink-500/40 bg-pink-500/[0.06]",
    rose: "text-rose-300 border-rose-500/40 bg-rose-500/[0.06]",
  };
  return (
    <div data-testid={testid} className={`p-4 rounded-lg border ${tones[tone]}`}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-muted-foreground"><Icon className="w-3.5 h-3.5"/> {label}</div>
      <div className="mt-1 text-2xl font-display font-bold">{value}</div>
    </div>
  );
}

function DeductionForm({ agent, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [f, setF] = useState({ effective_date: today, period: today.slice(0, 7), amount: 0, reason: "" });
  async function submit(e) {
    e.preventDefault();
    if (!f.reason?.trim()) { toast.error("Reason is required"); return; }
    try {
      await api.post("/sales-deductions", {
        agent_id: agent.id,
        amount: parseFloat(f.amount) || 0,
        reason: f.reason.trim(),
        effective_date: f.effective_date,
        period: f.period || null,
      });
      toast.success("Deduction recorded");
      setF({ effective_date: today, period: today.slice(0, 7), amount: 0, reason: "" });
      onSaved?.();
    } catch (err) { toast.error(formatErr(err.response?.data?.detail) || "Failed"); }
  }
  return (
    <form onSubmit={submit} className="grid grid-cols-2 md:grid-cols-6 gap-2 items-end">
      <div>
        <Label className="text-[10px] uppercase text-muted-foreground">Effective date</Label>
        <Input type="date" value={f.effective_date} onChange={(e) => setF({ ...f, effective_date: e.target.value })} data-testid="ded-effective" className="bg-[hsl(224_40%_12%)] mt-1 h-9"/>
      </div>
      <div>
        <Label className="text-[10px] uppercase text-muted-foreground">Period (YYYY-MM)</Label>
        <Input type="month" value={f.period} onChange={(e) => setF({ ...f, period: e.target.value })} data-testid="ded-period" className="bg-[hsl(224_40%_12%)] mt-1 h-9"/>
      </div>
      <div>
        <Label className="text-[10px] uppercase text-muted-foreground">Amount ₹</Label>
        <Input type="number" step="0.01" min="0.01" required value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="ded-amount" className="bg-[hsl(224_40%_12%)] mt-1 h-9"/>
      </div>
      <div className="md:col-span-2">
        <Label className="text-[10px] uppercase text-rose-300">Reason (required)</Label>
        <Input required value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} placeholder="e.g. Missed follow-up · Customer complaint · Policy breach" data-testid="ded-reason" className="bg-[hsl(224_40%_12%)] mt-1 h-9 border-rose-500/30 focus-visible:ring-rose-500/40"/>
      </div>
      <div className="flex items-end">
        <Button type="submit" data-testid="ded-save" className="w-full h-9 bg-rose-500 text-white hover:bg-rose-400 font-semibold">Deduct</Button>
      </div>
    </form>
  );
}

function PayoutForm({ agent, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [f, setF] = useState({ paid_on: today, period: today.slice(0, 7), amount: 0, method: "upi", note: "", reference: "" });
  async function submit(e) {
    e.preventDefault();
    try {
      await api.post("/sales-payouts", {
        agent_id: agent.id,
        amount: parseFloat(f.amount) || 0,
        paid_on: f.paid_on,
        period: f.period || null,
        note: f.note || null,
        method: f.method || null,
        reference: f.reference || null,
      });
      toast.success("Payout recorded");
      setF({ paid_on: today, period: today.slice(0, 7), amount: 0, method: "upi", note: "", reference: "" });
      onSaved?.();
    } catch (err) { toast.error(formatErr(err.response?.data?.detail) || "Failed"); }
  }
  return (
    <form onSubmit={submit} className="grid grid-cols-2 md:grid-cols-6 gap-2 items-end">
      <div><Label className="text-[10px] uppercase text-muted-foreground">Paid on</Label>
        <Input type="date" value={f.paid_on} onChange={(e) => setF({ ...f, paid_on: e.target.value })} data-testid="payout-paid-on" className="bg-[hsl(224_40%_12%)] mt-1 h-9"/></div>
      <div><Label className="text-[10px] uppercase text-muted-foreground">Period (YYYY-MM)</Label>
        <Input type="month" value={f.period} onChange={(e) => setF({ ...f, period: e.target.value })} data-testid="payout-period" className="bg-[hsl(224_40%_12%)] mt-1 h-9"/></div>
      <div><Label className="text-[10px] uppercase text-muted-foreground">Amount ₹</Label>
        <Input type="number" step="0.01" min="0" required value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="payout-amount" className="bg-[hsl(224_40%_12%)] mt-1 h-9"/></div>
      <div><Label className="text-[10px] uppercase text-muted-foreground">Method</Label>
        <select value={f.method} onChange={(e) => setF({ ...f, method: e.target.value })} data-testid="payout-method" className="w-full h-9 mt-1 rounded-md bg-[hsl(224_40%_12%)] border border-border text-sm px-2">
          <option value="upi">UPI</option><option value="bank">Bank transfer</option><option value="cash">Cash</option><option value="cheque">Cheque</option>
        </select>
      </div>
      <div><Label className="text-[10px] uppercase text-muted-foreground">Reference</Label>
        <Input value={f.reference} onChange={(e) => setF({ ...f, reference: e.target.value })} placeholder="UTR / Txn ID" data-testid="payout-ref" className="bg-[hsl(224_40%_12%)] mt-1 h-9"/></div>
      <div className="flex items-end"><Button type="submit" data-testid="payout-save" className="w-full h-9 bg-emerald-500 text-[hsl(224_60%_6%)] hover:bg-emerald-400 font-semibold">Record payout</Button></div>
      <div className="col-span-2 md:col-span-6">
        <Label className="text-[10px] uppercase text-muted-foreground">Note (optional)</Label>
        <Input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="payout-note" className="bg-[hsl(224_40%_12%)] mt-1 h-9"/>
      </div>
    </form>
  );
}

function PerfDialog({ agent, open, onOpenChange }) {
  const [perf, setPerf] = useState(null);
  async function reload() {
    if (!agent) return;
    setPerf(null);
    try {
      const { data } = await api.get(`/sales-agents/${agent.id}/performance`);
      setPerf(data);
    } catch { setPerf(null); }
  }
  useEffect(() => { if (open && agent) reload(); }, [open, agent?.id]);
  async function delPayout(id) {
    if (!window.confirm("Delete this payout entry?")) return;
    try { await api.delete(`/sales-payouts/${id}`); toast.success("Deleted"); reload(); }
    catch (err) { toast.error(formatErr(err.response?.data?.detail) || "Failed"); }
  }
  if (!agent) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[hsl(224_45%_9%)] border-border max-w-5xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-amber-400"/> Performance · {agent.name}
          </DialogTitle>
        </DialogHeader>
        {!perf ? <div className="py-8 text-center text-muted-foreground">Loading…</div> : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <StatTile testid="perf-leads" icon={UsersIcon} label="Assigned leads" value={perf.total_leads} tone="sky"/>
              <StatTile testid="perf-confirmed" icon={BadgeCheck} label="Confirmed leads" value={perf.confirmed_leads} tone="emerald"/>
              <StatTile testid="perf-trips" icon={TrendingUp} label="Confirmed vehicles" value={perf.confirmed_trips} tone="amber"/>
              <StatTile testid="perf-commission" icon={Percent} label={`Earned (${perf.commission_pct}%)`} value={fmt(perf.commission_earned)} tone="pink"/>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <StatTile testid="perf-earned2" icon={TrendingUp} label="Total earned" value={fmt(perf.commission_earned)} tone="pink"/>
              <StatTile testid="perf-paid2" icon={BadgeCheck} label="Total paid out" value={fmt(perf.commission_paid)} tone="emerald"/>
              <StatTile testid="perf-pending2" icon={Wallet} label="Pending balance" value={fmt(perf.commission_pending)} tone={perf.commission_pending > 0 ? "amber" : "emerald"}/>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <StatTile testid="perf-earned" icon={TrendingUp} label="Earned (gross)" value={fmt(perf.commission_earned)} tone="pink"/>
              <StatTile testid="perf-deducted" icon={MinusCircle} label="Deducted" value={fmt(perf.commission_deducted || 0)} tone="rose"/>
              <StatTile testid="perf-paid" icon={BadgeCheck} label="Paid out" value={fmt(perf.commission_paid)} tone="emerald"/>
              <StatTile testid="perf-pending" icon={Wallet} label="Pending balance" value={fmt(perf.commission_pending)} tone={perf.commission_pending > 0 ? "amber" : "emerald"}/>
            </div>

            <Card className="p-4 bg-[hsl(224_40%_11%)] border-border">
              <div className="flex items-center gap-2 mb-3">
                <MinusCircle className="w-4 h-4 text-rose-400"/>
                <div className="text-sm font-semibold text-rose-300">Deduct commission (admin only)</div>
                <div className="ml-auto text-[10px] text-muted-foreground">Reduces the agent's earned balance. A reason is mandatory and visible to the agent.</div>
              </div>
              <DeductionForm agent={agent} onSaved={reload}/>
            </Card>

            {(perf.deductions || []).length > 0 && (
              <Card className="p-4 bg-[hsl(224_40%_11%)] border-border">
                <div className="flex items-center gap-2 mb-3">
                  <MinusCircle className="w-4 h-4 text-rose-400"/>
                  <div className="text-sm font-semibold text-rose-300">Deduction history ({perf.deductions.length})</div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[640px] text-xs">
                    <thead><tr className="text-[10px] uppercase text-muted-foreground">
                      <th className="text-left p-2">Date</th><th className="text-left p-2">Period</th>
                      <th className="text-right p-2">Amount</th><th className="text-left p-2">Reason</th>
                      <th className="text-left p-2">By</th><th className="text-right p-2">Actions</th>
                    </tr></thead>
                    <tbody>
                      {perf.deductions.map((d) => (
                        <tr key={d.id} data-testid={`ded-row-${d.id}`} className="border-t border-border/60">
                          <td className="p-2 font-mono">{d.effective_date}</td>
                          <td className="p-2 font-mono text-amber-300">{d.period || "—"}</td>
                          <td className="p-2 text-right font-mono text-rose-300">-{fmt(d.amount)}</td>
                          <td className="p-2 text-slate-200 max-w-sm">{d.reason}</td>
                          <td className="p-2 text-muted-foreground">{d.created_by_name || "—"}</td>
                          <td className="p-2 text-right">
                            <Button variant="ghost" size="icon" data-testid={`del-ded-${d.id}`} className="h-7 w-7 hover:text-red-400" onClick={async () => {
                              if (!window.confirm("Reverse this deduction?")) return;
                              try { await api.delete(`/sales-deductions/${d.id}`); toast.success("Reversed"); reload(); }
                              catch (err) { toast.error(formatErr(err.response?.data?.detail) || "Failed"); }
                            }}><Trash2 className="w-3.5 h-3.5"/></Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}

            <Card className="p-4 bg-[hsl(224_40%_11%)] border-border">
              <div className="flex items-center gap-2 mb-3">
                <Wallet className="w-4 h-4 text-emerald-300"/>
                <div className="text-sm font-semibold text-emerald-300">Record a commission payout</div>
              </div>
              <PayoutForm agent={agent} onSaved={reload}/>
            </Card>

            <Card className="p-4 bg-[hsl(224_40%_11%)] border-border">
              <div className="flex items-center gap-2 mb-3">
                <BadgeCheck className="w-4 h-4 text-amber-300"/>
                <div className="text-sm font-semibold text-amber-300">Payout history ({perf.payouts.length})</div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[640px] text-xs">
                  <thead><tr className="text-[10px] uppercase text-muted-foreground">
                    <th className="text-left p-2">Paid on</th><th className="text-left p-2">Period</th>
                    <th className="text-right p-2">Amount</th><th className="text-left p-2">Method</th>
                    <th className="text-left p-2">Reference</th><th className="text-left p-2">Note</th>
                    <th className="text-right p-2">Actions</th>
                  </tr></thead>
                  <tbody>
                    {perf.payouts.length === 0 ? <tr><td colSpan={7} className="p-4 text-center text-muted-foreground">No payouts recorded yet.</td></tr> :
                      perf.payouts.map((p) => (
                        <tr key={p.id} data-testid={`payout-row-${p.id}`} className="border-t border-border/60">
                          <td className="p-2 font-mono">{p.paid_on}</td>
                          <td className="p-2 font-mono text-amber-300">{p.period || "—"}</td>
                          <td className="p-2 text-right font-mono text-emerald-300">{fmt(p.amount)}</td>
                          <td className="p-2 uppercase text-[10px]">{p.method || "—"}</td>
                          <td className="p-2 font-mono">{p.reference || "—"}</td>
                          <td className="p-2 text-muted-foreground">{p.note || "—"}</td>
                          <td className="p-2 text-right">
                            <Button variant="ghost" size="icon" data-testid={`del-payout-${p.id}`} className="h-7 w-7 hover:text-red-400" onClick={() => delPayout(p.id)}><Trash2 className="w-3.5 h-3.5"/></Button>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </Card>

            <div className="grid md:grid-cols-2 gap-4">
              <Card className="p-4 bg-[hsl(224_40%_11%)] border-border">
                <div className="text-sm font-semibold text-amber-300 mb-2">Per-month earnings</div>
                <div className="max-h-56 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead><tr className="text-[10px] uppercase text-muted-foreground"><th className="text-left p-2">Month</th><th className="text-right p-2">Trips</th><th className="text-right p-2">Profit</th><th className="text-right p-2">Earned</th><th className="text-right p-2">Paid</th></tr></thead>
                    <tbody>
                      {perf.monthly_stats.length === 0 ? <tr><td colSpan={5} className="p-4 text-center text-muted-foreground">No confirmed trips yet.</td></tr> :
                        perf.monthly_stats.map((m) => (
                          <tr key={m.month} className="border-t border-border/50">
                            <td className="p-2 font-mono">{m.month}</td>
                            <td className="p-2 text-right font-mono">{m.trips}</td>
                            <td className="p-2 text-right font-mono text-emerald-300">{fmt(m.profit)}</td>
                            <td className="p-2 text-right font-mono text-pink-300">{fmt(m.commission)}</td>
                            <td className="p-2 text-right font-mono text-emerald-200">{fmt(m.paid || 0)}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </Card>
              <Card className="p-4 bg-[hsl(224_40%_11%)] border-border">
                <div className="text-sm font-semibold text-amber-300 mb-2">Per-year earnings</div>
                <div className="max-h-56 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead><tr className="text-[10px] uppercase text-muted-foreground"><th className="text-left p-2">Year</th><th className="text-right p-2">Trips</th><th className="text-right p-2">Profit</th><th className="text-right p-2">Earned</th><th className="text-right p-2">Paid</th></tr></thead>
                    <tbody>
                      {perf.yearly_stats.length === 0 ? <tr><td colSpan={5} className="p-4 text-center text-muted-foreground">—</td></tr> :
                        perf.yearly_stats.map((y) => (
                          <tr key={y.year} className="border-t border-border/50">
                            <td className="p-2 font-mono">{y.year}</td>
                            <td className="p-2 text-right font-mono">{y.trips}</td>
                            <td className="p-2 text-right font-mono text-emerald-300">{fmt(y.profit)}</td>
                            <td className="p-2 text-right font-mono text-pink-300">{fmt(y.commission)}</td>
                            <td className="p-2 text-right font-mono text-emerald-200">{fmt(y.paid || 0)}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function SalesAgents() {
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [perfOpen, setPerfOpen] = useState(false);
  const [perfAgent, setPerfAgent] = useState(null);

  async function load() {
    try {
      const [a, u] = await Promise.all([
        api.get("/sales-agents"),
        api.get("/users").catch(() => ({ data: [] })),
      ]);
      setRows(a.data);
      setUsers((u.data || []).filter((x) => x.role === "sales"));
    } catch (err) { toast.error(formatErr(err.response?.data?.detail) || "Failed to load"); }
  }
  useEffect(() => { load(); }, []);

  async function save(f) {
    try {
      if (editing) await api.put(`/sales-agents/${editing.id}`, f);
      else await api.post("/sales-agents", f);
      toast.success("Saved");
      setOpen(false); setEditing(null); load();
    } catch (err) { toast.error(formatErr(err.response?.data?.detail) || "Failed"); }
  }

  async function del(id) {
    if (!window.confirm("Delete this sales agent?")) return;
    try {
      const res = await api.delete(`/sales-agents/${id}`);
      toast.success(res.status === 202 ? "Delete request sent to admin" : "Deleted");
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      if (err.response?.status === 202) { toast.success("Delete request sent to admin"); return; }
      toast.error(formatErr(d) || "Failed");
    }
  }

  const filtered = useMemo(
    () => rows.filter((r) => JSON.stringify(r).toLowerCase().includes(q.toLowerCase())),
    [rows, q]
  );

  return (
    <div>
      <PageHeader
        title="Sales & Marketing Agents"
        subtitle="Manage sales team, set commission %, and monitor per-agent performance."
        right={
          <>
            <ExportBar
              rows={filtered.map((r) => ({
                date: r.entry_date, name: r.name, email: r.email,
                employee_id: r.employee_id, mobile: r.mobile, whatsapp: r.whatsapp,
                commission_pct: r.commission_pct,
              }))}
              filename="sales-agents"
              whatsappText={`Sales & marketing agents: ${filtered.length}`}
            />
            <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
              <DialogTrigger asChild>
                <Button data-testid="add-sales-agent-btn" className="bg-amber-500 text-[hsl(224_60%_6%)] hover:bg-amber-400 font-semibold gap-2">
                  <Plus className="w-4 h-4"/> New agent
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[hsl(224_45%_9%)] border-border">
                <DialogHeader><DialogTitle className="font-display text-2xl">{editing ? "Edit sales agent" : "New sales agent"}</DialogTitle></DialogHeader>
                <AgentForm initial={editing} users={users} onSubmit={save}/>
              </DialogContent>
            </Dialog>
          </>
        }
      />

      <Card className="bg-[hsl(224_45%_9%)] border-border">
        <div className="p-4 border-b border-border flex items-center gap-3">
          <Search className="w-4 h-4 text-muted-foreground"/>
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, email, employee ID…" data-testid="sa-filter" className="bg-transparent border-0 focus-visible:ring-0 h-8"/>
          <div className="text-xs text-muted-foreground">{filtered.length} of {rows.length}</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[880px] text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-muted-foreground bg-[hsl(224_40%_11%)]">
                {["Date","Employee ID","Name","Email","Mobile","WhatsApp","Commission","Actions"].map((h, i) => (
                  <th key={h} className={`text-left p-3 ${i === 0 ? "sticky left-0 z-10 bg-[hsl(224_40%_11%)] min-w-[110px]" : ""}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={8} className="p-8 text-center text-muted-foreground">No sales agents yet.</td></tr>
              ) : filtered.map((r) => (
                <tr key={r.id} className="border-t border-border bg-[hsl(224_45%_9%)]" data-testid={`sa-row-${r.id}`}>
                  <td className="p-3 font-mono text-xs sticky left-0 z-10 bg-[hsl(224_45%_9%)] min-w-[110px]">{r.entry_date || r.created_at?.slice(0, 10)}</td>
                  <td className="p-3 font-mono text-amber-300">{r.employee_id}</td>
                  <td className="p-3">{r.name}</td>
                  <td className="p-3 font-mono text-xs">{r.email}</td>
                  <td className="p-3 font-mono text-xs">{r.mobile || "—"}</td>
                  <td className="p-3 font-mono text-xs text-emerald-300">{r.whatsapp || "—"}</td>
                  <td className="p-3"><Badge className="bg-pink-500/15 text-pink-300 border-pink-500/40">{parseFloat(r.commission_pct || 0).toFixed(2)}%</Badge></td>
                  <td className="p-3">
                    <div className="inline-flex gap-1">
                      <Button variant="ghost" size="icon" data-testid={`sa-perf-${r.id}`} className="h-8 w-8 hover:text-amber-300" title="Performance" onClick={() => { setPerfAgent(r); setPerfOpen(true); }}>
                        <BarChart3 className="w-3.5 h-3.5"/>
                      </Button>
                      <Button variant="ghost" size="icon" data-testid={`sa-edit-${r.id}`} className="h-8 w-8" onClick={() => { setEditing(r); setOpen(true); }}>
                        <Pencil className="w-3.5 h-3.5"/>
                      </Button>
                      <Button variant="ghost" size="icon" data-testid={`sa-del-${r.id}`} className="h-8 w-8 hover:text-red-400" onClick={() => del(r.id)}>
                        <Trash2 className="w-3.5 h-3.5"/>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <div className="mt-4 flex items-center gap-2 text-xs text-muted-foreground">
        <Megaphone className="w-3.5 h-3.5 text-amber-400"/>
        Assign leads to a sales agent from the Leads page. Attribute confirmed vehicles from the Trips form via the "Sales agent" field. Commission = <span className="text-emerald-300 font-mono">Trip Profit</span> × agent % .
      </div>

      <PerfDialog agent={perfAgent} open={perfOpen} onOpenChange={setPerfOpen}/>
    </div>
  );
}
